import { faNoteSticky, faExternalLink } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { JSX, memo, useContext, useMemo } from 'react';

import { ActivityLogEvent } from '@/constants/activityLogEvent';
import { Color, ColorBorderLight, ColorVariantsLight } from '@/constants/color';
import AppContext from '@/contexts/appContext';
import useAPI from '@/hooks/useAPI';
import PersonContext from '@/pages/Person/PersonContext';
import ConversationStatusAndTags from '@/pages/Timeline/components/ConversationStatusAndTags/ConversationStatusAndTags';
import TimelineHeader from '@/pages/Timeline/components/TimelineHeader';
import TimestampAndActor from '@/pages/Timeline/components/TimestampAndActor';
import { TimelineSubcomponentProps } from '@/pages/Timeline/types';
import KustomerNoteRepository from '@/repositories/KustomerNoteRepository';
import Button from '@/ui/components/Button/Button';
import Card from '@/ui/components/Card';
import Spinner from '@/ui/components/Spinner';
import { kustomerEventLink } from '@/utils/kustomerLinks';

const NoteEvent = ({ activityLog }: TimelineSubcomponentProps): JSX.Element => {
  const { action, actor, data: activityLogData, object_id, timestamp } = activityLog;
  const { host } = useContext(AppContext);
  const {
    person: { kustomer_id: personKustomerId }
  } = useContext(PersonContext);
  const kustomerNoteRepository = KustomerNoteRepository();

  const {
    data: note,
    isFetching: isFetchingNote,
    refetch: getNote
  } = useAPI({
    queryKey: ['getKustomerNote', object_id],
    queryFn: async () => {
      return await kustomerNoteRepository.getKustomerNote(object_id);
    },
    enabled: false
  });

  const { isFetching: isFetchingConversationId, refetch: getKustomerConversationId } = useAPI({
    queryKey: ['getNoteKustomerConversationId', object_id],
    queryFn: async () => {
      return await kustomerNoteRepository.kustomerConversationId(object_id);
    },
    enabled: false
  });

  const noteDetails = () => {
    if (note) {
      return <ConversationStatusAndTags conversation={note.conversation} />;
    }

    return null;
  };

  const title = useMemo(
    () => () => {
      return action === ActivityLogEvent.kustomer_note_created
        ? 'Kustomer Note Created'
        : 'Note Created';
    },
    [action]
  );

  const handleViewInKustomerClick = async () => {
    const response = await getKustomerConversationId();

    if (response?.data) {
      const { kustomer_id } = response.data;
      window.open(
        kustomerEventLink({
          host,
          kustomerId: kustomer_id,
          personKustomerId: personKustomerId as string // value is asserted below
        }),
        '_blank'
      );
    }
  };

  return (
    <div>
      <Card>
        <TimelineHeader
          icon={faNoteSticky}
          subtitle={activityLogData?.conversation_name}
          title={
            <>
              {title()}{' '}
              {personKustomerId && (
                <button
                  aria-label="View in Kustomer"
                  className="ml-2"
                  disabled={isFetchingConversationId}
                  onClick={handleViewInKustomerClick}
                  type="button">
                  {isFetchingConversationId ? (
                    <span className="font-normal text-xs">
                      <Spinner />
                    </span>
                  ) : (
                    <FontAwesomeIcon icon={faExternalLink} className="text-gray-600" />
                  )}
                </button>
              )}
            </>
          }
        />

        {activityLogData?.body && (
          <div
            className={`my-1 p-2 rounded-lg ${ColorVariantsLight[Color.YELLOW]} ${
              ColorBorderLight[Color.YELLOW]
            }`}>
            {activityLogData.body}
          </div>
        )}

        <div className="clear-both">
          {!note && (
            <Button
              color={Color.WHITE}
              loading={isFetchingNote}
              loadingWithSpinner={false}
              onClick={() => getNote()}
              size="xs">
              Click for Status &amp; Tags
            </Button>
          )}

          {noteDetails()}
        </div>
      </Card>

      <TimestampAndActor actor={actor} timestamp={timestamp} />
    </div>
  );
};

export default memo(NoteEvent);
